import {
  getBase64EncodedWireTransaction,
  getSignatureFromTransaction,
  getPublicKeyFromAddress,
  verifySignature,
  type Address,
  type Rpc,
  type SolanaRpcApi,
  type Transaction,
  type TransactionWithLifetime,
} from "@solana/kit";
import { z } from "zod";
import { API_BASE_URL, API_TIMEOUT, SOLANA_NETWORK } from "@/config/constants";
import { useAuthStore } from "@/store/auth-store";
import type { TransactionResult } from "@/types/solana";

export type SignableTransaction = Transaction & TransactionWithLifetime;
export type SignTransactionFn = (tx: SignableTransaction) => Promise<SignableTransaction>;
export interface TransferDetails {
  recipient: string;
  amount: string;
  asset: string;
}

const relayStatus = z.enum(["confirmed", "finalized", "submitted", "unknown", "failed"]);
const receiptSchema = z.object({
  data: z.object({ signature: z.string(), status: relayStatus }),
});
const operationSchema = z.object({
  version: z.literal(1),
  wallet: z.string(),
  operationId: z.uuid(),
  signature: z.string().min(64).max(88),
  transaction: z.string().min(1),
  recipient: z.string(),
  amount: z.string(),
  asset: z.string(),
  createdAt: z.number().int(),
  status: relayStatus,
});
export interface TransferOperation extends TransferDetails {
  version: 1;
  wallet: string;
  operationId: string;
  signature: string;
  transaction: string;
  createdAt: number;
  status: z.infer<typeof relayStatus>;
}

export function decimalToBaseUnits(amount: string | number, decimals: number): bigint {
  const text = String(amount);
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > 18 || !/^\d+(?:\.\d+)?$/.test(text)) {
    throw new Error("Invalid transfer amount");
  }
  const [whole = "0", fraction = ""] = text.split(".");
  if (fraction.length > decimals) throw new Error("Amount exceeds token precision");
  const units = BigInt(whole + fraction.padEnd(decimals, "0"));
  if (units <= 0n || units > 18_446_744_073_709_551_615n) throw new Error("Invalid transfer amount");
  return units;
}

export function walletTransactionLockKey(wallet: string): string {
  return `detrust-transfer-v1:${API_BASE_URL}:${SOLANA_NETWORK}:${wallet}`;
}
function isTerminal(status: TransferOperation["status"]): boolean {
  return status === "confirmed" || status === "finalized" || status === "failed";
}
function resultFor(operation: TransferOperation): TransactionResult {
  return {
    signature: operation.signature,
    operationId: operation.operationId,
    status: operation.status,
    confirmed: operation.status === "confirmed" || operation.status === "finalized",
  };
}
function save(operation: TransferOperation): void {
  const serialized = JSON.stringify(operation);
  localStorage.setItem(walletTransactionLockKey(operation.wallet), serialized);
  if (localStorage.getItem(walletTransactionLockKey(operation.wallet)) !== serialized) {
    throw new Error("Could not save the signed transaction for recovery");
  }
}
function read(wallet: string): TransferOperation | null {
  const value = localStorage.getItem(walletTransactionLockKey(wallet));
  if (value === null) return null;
  const operation = operationSchema.parse(JSON.parse(value));
  if (operation.wallet !== wallet) throw new Error("Saved transaction belongs to another wallet");
  return operation;
}
function authHeaders(wallet: string): Record<string, string> {
  const auth = useAuthStore.getState();
  if (!auth.token || auth.wallet !== wallet || auth.isTokenExpired()) {
    throw new Error("Sign in with the sending wallet to continue");
  }
  return { "Content-Type": "application/json", Authorization: `Bearer ${auth.token}` };
}

async function requestStatus(operation: TransferOperation, submit: boolean): Promise<TransactionResult> {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/relay/${submit ? "transaction" : `status/${encodeURIComponent(operation.signature)}`}`,
      {
        method: submit ? "POST" : "GET",
        headers: authHeaders(operation.wallet),
        ...(submit ? { body: JSON.stringify({ transaction: operation.transaction, encoding: "base64", operationId: operation.operationId }) } : {}),
        signal: AbortSignal.timeout(API_TIMEOUT),
      },
    );
    if (!response.ok) throw new Error(`Transaction status unavailable (HTTP ${response.status})`);
    const { data } = receiptSchema.parse(await response.json());
    if (data.signature !== operation.signature) throw new Error("Relay returned a different transaction signature");
    const updated: TransferOperation = { ...operation, status: data.status };
    // A status write failure must leave the durable unknown record in place.
    if (read(operation.wallet)?.operationId !== operation.operationId) throw new Error("Saved operation changed");
    save(updated);
    return resultFor(updated);
  } catch {
    return { ...resultFor(operation), status: "unknown", confirmed: false,
      message: "The transaction outcome is unknown. Check the original signature before sending again." };
  }
}

/** Verify the exact bytes reviewed before entering a wallet signer. */
export async function verifyReviewedWalletTransaction(wallet: Address, reviewedMessage: Uint8Array, signed: Transaction): Promise<void> {
  if (signed.messageBytes.length !== reviewedMessage.length ||
    signed.messageBytes.some((byte, index) => byte !== reviewedMessage[index])) {
    throw new Error("Wallet changed the reviewed transaction");
  }
  if (Object.keys(signed.signatures)[0] !== wallet) throw new Error("Sending wallet must be the transaction fee payer");
  const signatureBytes = signed.signatures[wallet];
  if (!signatureBytes || !signatureBytes.some((byte) => byte !== 0)) throw new Error("Wallet signature is missing");
  if (!await verifySignature(await getPublicKeyFromAddress(wallet), signatureBytes, reviewedMessage)) {
    throw new Error("Wallet signature is invalid");
  }
}

export const transactionRelay = {
  read,
  async recover(wallet: string): Promise<TransactionResult | null> {
    if (!navigator.locks) throw new Error("This browser cannot safely coordinate wallet transactions");
    return navigator.locks.request(walletTransactionLockKey(wallet), { ifAvailable: true }, async (lock) => {
      const operation = read(wallet);
      if (!operation) return null;
      if (!lock) return { ...resultFor(operation), status: "unknown", confirmed: false };
      // Even a saved terminal display is checked against this operation's receipt.
      return requestStatus(operation, false);
    });
  },
  async acknowledge(wallet: string, operationId: string): Promise<void> {
    if (!navigator.locks) throw new Error("This browser cannot safely coordinate wallet transactions");
    await navigator.locks.request(walletTransactionLockKey(wallet), { ifAvailable: true }, (lock) => {
      if (!lock) throw new Error("Another transaction is being checked for this wallet");
      const operation = read(wallet);
      if (!operation) return;
      if (operation.operationId !== operationId || !isTerminal(operation.status)) throw new Error("The saved transaction still needs its own receipt");
      localStorage.removeItem(walletTransactionLockKey(wallet));
    });
  },
  async execute(
    wallet: Address,
    details: TransferDetails,
    build: () => Promise<SignableTransaction>,
    sign: SignTransactionFn,
    rpc: Rpc<SolanaRpcApi>,
  ): Promise<TransactionResult> {
    try {
      // Cross-tab exclusion covers building, wallet confirmation, persistence and
      // transport. ifAvailable prevents a queued duplicate from becoming a new send.
      if (!navigator.locks) throw new Error("This browser cannot safely coordinate wallet transactions");
      return await navigator.locks.request(walletTransactionLockKey(wallet), { ifAvailable: true }, async (lock) => {
        if (!lock) throw new Error("Another transaction is being prepared for this wallet");
        const previous = read(wallet);
        if (previous) return requestStatus(previous, false);
        authHeaders(wallet);
        const transaction = await build();
        const { value: simulation } = await rpc.simulateTransaction(getBase64EncodedWireTransaction(transaction), {
          encoding: "base64", sigVerify: false,
        }).send({ abortSignal: AbortSignal.timeout(API_TIMEOUT) });
        if (simulation.err !== null) {
          return { signature: "", status: "not_submitted", confirmed: false, error: "SimulationFailed",
            message: "The transaction simulation failed. No transaction was submitted." };
        }
        const reviewedMessage = new Uint8Array(transaction.messageBytes);
        const signed = await sign(transaction);
        await verifyReviewedWalletTransaction(wallet, reviewedMessage, signed);
        const signature = getSignatureFromTransaction(signed);
        const operation: TransferOperation = {
          version: 1, wallet, ...details, operationId: crypto.randomUUID(), signature,
          transaction: getBase64EncodedWireTransaction(signed), createdAt: Date.now(), status: "unknown",
        };
        // A refresh from here onward can only recover this signature. Never
        // rebuild/re-sign after a request starts, including blockhash expiry.
        save(operation);
        return requestStatus(operation, true);
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Transaction preparation failed";
      return { signature: "", status: "not_submitted", confirmed: false,
        error: /reject|cancel|denied/i.test(message) ? "UserRejected" : "Unknown", message };
    }
  },
};
